import Phaser from 'phaser';
import { Direction } from '@shared/types/entity';
import { GAME_FONT_FAMILY, TILE_SIZE } from '@shared/constants';
import { getAllItemTemplates } from '@shared/data/items';
import {
  getBodySpriteKey,
  getBodyCenterOffset,
  getBodyAnimKey,
} from '../../sprites/BodySpriteManager';
import { getHeadSpriteKey, getHeadFrameForDirection } from '../../sprites/HeadSpriteManager';
import {
  getWeaponIdleFrame,
  getWeaponAssetName,
  getWeaponSheetOffsetByName,
} from '../../sprites/WeaponSpriteManager';
import { addGridSpriteSheet } from '../../sprites/gridSpriteSheet';

/**
 * Galería de WEAPONS — preview client-side (no equipa nada). Espejo de
 * BodyGalleryPanel pero el grid 2x2 muestra el personaje (body + cabeza) con el
 * ARMA seleccionada encima, animados caminando en cada dirección.
 *
 * La lista de la izquierda se arma enumerando TODOS los PNG de
 * `client/assets/weapon/` (vía import.meta.glob), no sólo las armas que tienen un
 * item asignado. Así sirve para debuggear cualquier sprite suelto de la carpeta.
 * Cada arma se carga/recorta por NOMBRE de archivo (no por weaponId).
 */

// Enumeración de archivos de la carpeta de weapons en build-time (Vite). Sólo se
// usan las KEYS (rutas) para obtener los nombres; no se importan los módulos.
const WEAPON_PNG_GLOB = import.meta.glob('../../../assets/weapon/anims/*.png');

function listWeaponAssetNames(): string[] {
  const names = Object.keys(WEAPON_PNG_GLOB)
    .map((p) => p.split('/').pop() ?? '')
    .map((f) => f.replace(/\.png$/i, ''))
    .filter((n) => n.length > 0 && n.toLowerCase() !== 'readme');
  return Array.from(new Set(names)).sort((a, b) =>
    a.localeCompare(b, undefined, { numeric: true })
  );
}

function withGameFont<T extends Phaser.Types.GameObjects.Text.TextStyle>(style?: T): T {
  return {
    fontFamily: GAME_FONT_FAMILY,
    resolution: 2,
    ...style,
  } as T;
}

type WeaponGalleryPanelCallbacks = {
  onMove(x: number, y: number): void;
  getLocalPlayerBodyId(): number;
  getLocalPlayerHeadId(): number;
  // Preview client-side sobre el personaje real. '' = quitar override.
  onSelectWeapon(name: string): void;
};

type WeaponGalleryPanelOptions = {
  initialX: number;
  initialY: number;
  BODY_FRAME_HEIGHT: number;
  HEAD_OFFSET_FROM_BODY_BOTTOM: number;
  HEAD_FRAME_HEIGHT: number;
  WEAPON_OFFSET_X: number;
  WEAPON_OFFSET_Y: number;
  WEAPON_LATERAL_DX: number;
};

type PreviewCell = {
  direction: Direction;
  cx: number;
  cy: number;
  cellW: number;
  cellH: number;
  bodyBottomY: number;
  box: Phaser.GameObjects.Rectangle;
  body: Phaser.GameObjects.Sprite;
  weapon: Phaser.GameObjects.Sprite;
  head: Phaser.GameObjects.Sprite;
  vLine: Phaser.GameObjects.Rectangle;
  hLine: Phaser.GameObjects.Rectangle;
  label: Phaser.GameObjects.Text;
};

type WeaponGalleryPanelElements = {
  bg: Phaser.GameObjects.Rectangle;
  listBg: Phaser.GameObjects.Rectangle;
  title: Phaser.GameObjects.Text;
  list: Phaser.GameObjects.Text[];
  previewCells: PreviewCell[];
  closeButton: Phaser.GameObjects.Text;
  scrollUp: Phaser.GameObjects.Text;
  scrollDown: Phaser.GameObjects.Text;
  panelX: number;
  panelY: number;
  panelWidth: number;
  panelHeight: number;
  colPadding: number;
  listColWidth: number;
  previewColWidth: number;
  previewStartX: number;
  previewCenterX: number;
  rowHeight: number;
  listStartY: number;
};

const CELL_DIRECTIONS: Direction[] = [Direction.UP, Direction.DOWN, Direction.LEFT, Direction.RIGHT];

const CELL_DIR_LABEL: Record<Direction, string> = {
  [Direction.UP]: 'ARRIBA',
  [Direction.DOWN]: 'ABAJO',
  [Direction.LEFT]: 'IZQ',
  [Direction.RIGHT]: 'DER',
};

// Fila del atlas (6x4) por dirección: DOWN=0, UP=1, LEFT=2, RIGHT=3 (igual que
// getWeaponIdleFrame). Las cols 1..4 son el walk; col 0 es idle.
const WEAPON_GRID_COLUMNS = 6;
const WEAPON_GRID_ROWS = 4;
const DIR_ROW: Record<Direction, number> = {
  [Direction.DOWN]: 0,
  [Direction.UP]: 1,
  [Direction.LEFT]: 2,
  [Direction.RIGHT]: 3,
};

type WeaponEntry = { name: string; label: string };

export class WeaponGalleryPanel {
  private readonly scene: Phaser.Scene;
  private readonly callbacks: WeaponGalleryPanelCallbacks;
  private readonly constants: {
    BODY_FRAME_HEIGHT: number;
    HEAD_OFFSET_FROM_BODY_BOTTOM: number;
    HEAD_FRAME_HEIGHT: number;
    WEAPON_OFFSET_X: number;
    WEAPON_OFFSET_Y: number;
    WEAPON_LATERAL_DX: number;
  };
  private panel: WeaponGalleryPanelElements | null = null;
  private isOpen = false;
  private isDragging = false;
  private dragOffsetX = 0;
  private dragOffsetY = 0;
  private readonly weapons: WeaponEntry[] = [];
  private selectedName: string = '';
  private scrollOffset: number = 0;

  constructor(
    scene: Phaser.Scene,
    callbacks: WeaponGalleryPanelCallbacks,
    options: WeaponGalleryPanelOptions
  ) {
    this.scene = scene;
    this.callbacks = callbacks;
    this.constants = {
      BODY_FRAME_HEIGHT: options.BODY_FRAME_HEIGHT,
      HEAD_OFFSET_FROM_BODY_BOTTOM: options.HEAD_OFFSET_FROM_BODY_BOTTOM,
      HEAD_FRAME_HEIGHT: options.HEAD_FRAME_HEIGHT,
      WEAPON_OFFSET_X: options.WEAPON_OFFSET_X,
      WEAPON_OFFSET_Y: options.WEAPON_OFFSET_Y,
      WEAPON_LATERAL_DX: options.WEAPON_LATERAL_DX,
    };
    this.weapons = this.buildWeaponList();
    this.selectedName = this.weapons[0]?.name ?? '';
    this.buildPanel(options.initialX, options.initialY);
    this.setVisible(false);
    // Cargar y recortar todos los sheets de la carpeta (async). Al terminar
    // refresca el preview.
    this.ensureWeaponTextures();
    // Frame-lock del arma al cuerpo cada tick (mientras el panel está abierto).
    this.scene.events.on(Phaser.Scenes.Events.UPDATE, this.frameLockWeapons, this);
  }

  /**
   * Copia el frame ACTUAL del cuerpo a cada arma del preview (mismo r{row}c{col}),
   * igual que el render in-game. Evita el desfase de anims independientes.
   */
  private frameLockWeapons(): void {
    if (!this.isOpen || !this.panel) return;
    for (const cell of this.panel.previewCells) {
      if (!cell.weapon.visible) continue;
      const bodyFrameIndex = parseInt(cell.body.frame.name, 10);
      if (!Number.isFinite(bodyFrameIndex)) continue;
      const row = Math.floor(bodyFrameIndex / WEAPON_GRID_COLUMNS);
      const col = bodyFrameIndex % WEAPON_GRID_COLUMNS;
      const frameName = `r${row}c${col}`;
      if (cell.weapon.texture.has(frameName)) {
        cell.weapon.setFrame(frameName);
      }
    }
  }

  /**
   * Arma la lista de weapons enumerando TODOS los PNG de assets/weapon/.
   * Si el glob no encontró nada (build raro), cae a las armas referenciadas por
   * items.ts como fallback para no quedar vacío.
   */
  private buildWeaponList(): WeaponEntry[] {
    const names = listWeaponAssetNames();
    if (names.length > 0) {
      return names.map((name) => ({ name, label: name }));
    }
    // Fallback: derivar de los items con weaponId.
    const seen = new Set<string>();
    const out: WeaponEntry[] = [];
    for (const tpl of getAllItemTemplates()) {
      if (tpl.weaponId && tpl.weaponId > 0) {
        const name = tpl.icon ?? getWeaponAssetName(tpl.weaponId);
        if (name && !seen.has(name)) {
          seen.add(name);
          out.push({ name, label: name });
        }
      }
    }
    out.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
    return out;
  }

  /** Texture key del sheet recortado de la galería para un nombre de archivo. */
  private galleryKey(name: string): string {
    return `gwpreview_${name}`;
  }

  /** Anim key (walk) del sheet de la galería para nombre + dirección. */
  private galleryAnimKey(name: string, dir: Direction): string {
    return `gwpreview_${name}_${dir}`;
  }

  /**
   * Carga (si falta) cada PNG de la carpeta, lo recorta como spritesheet 6x4 con
   * frames `r{row}c{col}` y crea las anims de walk. Async: al completar refresca.
   */
  private ensureWeaponTextures(): void {
    const pending = this.weapons
      .map((w) => w.name)
      .filter(
        (name) =>
          !this.scene.textures.exists(this.galleryKey(name)) &&
          !this.scene.textures.exists(`${this.galleryKey(name)}-img`)
      );

    if (pending.length === 0) {
      this.createWeaponAnims();
      return;
    }

    for (const name of pending) {
      this.scene.load.image(`${this.galleryKey(name)}-img`, `assets/weapon/anims/${name}.png`);
    }

    this.scene.load.once(Phaser.Loader.Events.COMPLETE, () => {
      for (const name of pending) {
        const sheetKey = this.galleryKey(name);
        if (this.scene.textures.exists(sheetKey)) continue;
        const imgKey = `${sheetKey}-img`;
        if (!this.scene.textures.exists(imgKey)) continue;
        const source = this.scene.textures.get(imgKey).getSourceImage() as HTMLImageElement;
        if (!source) continue;
        addGridSpriteSheet(
          this.scene,
          sheetKey,
          source,
          WEAPON_GRID_COLUMNS,
          WEAPON_GRID_ROWS,
          (row, col) => `r${row}c${col}`
        );
      }
      this.createWeaponAnims();
      if (this.isOpen) this.refreshContents();
    });

    this.scene.load.start();
  }

  /** Crea las anims de walk (cols 1..4) por arma+dirección, una sola vez. */
  private createWeaponAnims(): void {
    for (const { name } of this.weapons) {
      const key = this.galleryKey(name);
      if (!this.scene.textures.exists(key)) continue;
      const tex = this.scene.textures.get(key);
      for (const dir of CELL_DIRECTIONS) {
        const animKey = this.galleryAnimKey(name, dir);
        if (this.scene.anims.exists(animKey)) continue;
        const row = DIR_ROW[dir];
        const frames: Phaser.Types.Animations.AnimationFrame[] = [];
        for (let col = 1; col <= 4; col += 1) {
          const frameName = `r${row}c${col}`;
          if (tex.has(frameName)) frames.push({ key, frame: frameName });
        }
        if (frames.length > 0) {
          this.scene.anims.create({ key: animKey, frames, frameRate: 6, repeat: -1 });
        }
      }
    }
  }

  private weaponOffsetX(dir: Direction): number {
    const lateral = dir === Direction.LEFT || dir === Direction.RIGHT;
    return this.constants.WEAPON_OFFSET_X + (lateral ? this.constants.WEAPON_LATERAL_DX : 0);
  }

  toggle(): void {
    this.isOpen = !this.isOpen;
    this.setVisible(this.isOpen);
  }

  open(): void {
    this.isOpen = true;
    this.setVisible(true);
  }

  close(): void {
    this.isOpen = false;
    this.setVisible(false);
  }

  isOpenPanel(): boolean {
    return this.isOpen;
  }

  handlePointerMove(pointer: Phaser.Input.Pointer): void {
    if (!this.isDragging || !this.panel) return;
    const newX = pointer.x - this.dragOffsetX;
    const newY = pointer.y - this.dragOffsetY;
    this.move(newX, newY);
    this.callbacks.onMove(newX, newY);
  }

  handlePointerUp(pointer: Phaser.Input.Pointer): void {
    if (this.isDragging) {
      this.isDragging = false;
      pointer.event.stopPropagation();
    }
  }

  isDraggingPanel(): boolean {
    return this.isDragging;
  }

  isPointerOver(pointer: Phaser.Input.Pointer): boolean {
    if (!this.panel || !this.isOpen) return false;
    const { panelX, panelY, panelWidth, panelHeight } = this.panel;
    const left = panelX - panelWidth / 2;
    const right = panelX + panelWidth / 2;
    const top = panelY - panelHeight / 2;
    const bottom = panelY + panelHeight / 2;
    return pointer.x >= left && pointer.x <= right && pointer.y >= top && pointer.y <= bottom;
  }

  private buildPanel(initialX: number, initialY: number): void {
    const panelWidth = 520;
    const panelHeight = 320;
    const panelX = initialX;
    const panelY = initialY;

    const bg = this.scene.add
      .rectangle(panelX, panelY, panelWidth, panelHeight, 0x111111, 0.9)
      .setOrigin(0.5)
      .setStrokeStyle(1, 0x666666)
      .setDepth(1100)
      .setScrollFactor(0)
      .setVisible(false);

    const colPadding = 12;
    const listColWidth = panelWidth * 0.45;
    const previewColWidth = panelWidth * 0.55 - colPadding * 3;

    const baseX = panelX - panelWidth / 2;
    const baseY = panelY - panelHeight / 2;
    const leftStartX = baseX + colPadding;
    const previewStartX = leftStartX + listColWidth + colPadding;

    const listBg = this.scene.add
      .rectangle(
        leftStartX + listColWidth / 2,
        panelY,
        listColWidth,
        panelHeight - colPadding * 2,
        0x222222,
        0.8
      )
      .setOrigin(0.5)
      .setStrokeStyle(1, 0x555555)
      .setDepth(1100)
      .setScrollFactor(0)
      .setVisible(false);

    const title = this.scene.add
      .text(panelX, baseY + 16, 'Galería de weapons', withGameFont({
        fontSize: '16px',
        color: '#ffffff',
      }))
      .setOrigin(0.5, 0.5)
      .setDepth(1101)
      .setScrollFactor(0)
      .setVisible(false)
      .setInteractive({ useHandCursor: true });

    title.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      this.isDragging = true;
      this.dragOffsetX = pointer.x - bg.x;
      this.dragOffsetY = pointer.y - bg.y;
      pointer.event.stopPropagation();
    });

    const scrollUp = this.scene.add
      .text(leftStartX + listColWidth - 8, baseY + 10, '▲', withGameFont({
        fontSize: '12px',
        color: '#ffffaa',
      }))
      .setOrigin(1, 0)
      .setDepth(1101)
      .setScrollFactor(0)
      .setVisible(false)
      .setInteractive({ useHandCursor: true });

    scrollUp.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      this.scrollOffset = Math.max(0, this.scrollOffset - (this.panel?.list.length ?? 14));
      this.refreshContents();
      pointer.event.stopPropagation();
    });

    const scrollDown = this.scene.add
      .text(leftStartX + listColWidth - 8, baseY + panelHeight - 26, '▼', withGameFont({
        fontSize: '12px',
        color: '#ffffaa',
      }))
      .setOrigin(1, 0)
      .setDepth(1101)
      .setScrollFactor(0)
      .setVisible(false)
      .setInteractive({ useHandCursor: true });

    scrollDown.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      const maxOffset = Math.max(0, this.weapons.length - (this.panel?.list.length ?? 14));
      this.scrollOffset = Math.min(maxOffset, this.scrollOffset + (this.panel?.list.length ?? 14));
      this.refreshContents();
      pointer.event.stopPropagation();
    });

    const listStartY = baseY + 36;
    const rowHeight = 18;
    const list: Phaser.GameObjects.Text[] = [];
    const visibleRows = 14;

    for (let i = 0; i < visibleRows; i += 1) {
      const y = listStartY + i * rowHeight;
      const txt = this.scene.add
        .text(leftStartX + 6, y, '', withGameFont({
          fontSize: '12px',
          color: '#ffffff',
        }))
        .setOrigin(0, 0)
        .setDepth(1101)
        .setScrollFactor(0)
        .setVisible(false)
        .setInteractive({ useHandCursor: true });

      const index = i;
      txt.on('pointerup', (pointer: Phaser.Input.Pointer) => {
        this.handleListClick(index);
        pointer.event.stopPropagation();
      });

      list.push(txt);
    }

    const previewCenterX = previewStartX + previewColWidth / 2;

    const cellLayout = this.computeCellLayout(previewStartX, baseY, previewColWidth, panelHeight);
    const previewCells: PreviewCell[] = CELL_DIRECTIONS.map((dir, idx) => {
      const { cx, cy, cellW, cellH, bodyBottomY } = cellLayout[idx];
      return this.createPreviewCell(dir, cx, cy, cellW, cellH, bodyBottomY);
    });

    const closeButton = this.scene.add
      .text(panelX + panelWidth / 2 - 16, baseY + 8, '✕', withGameFont({
        fontSize: '14px',
        color: '#ffffff',
        backgroundColor: '#555555',
      }))
      .setOrigin(1, 0)
      .setPadding(4, 2, 4, 2)
      .setDepth(1101)
      .setScrollFactor(0)
      .setVisible(false)
      .setInteractive({ useHandCursor: true });

    closeButton.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      this.close();
      pointer.event.stopPropagation();
    });

    this.panel = {
      bg,
      listBg,
      title,
      list,
      previewCells,
      closeButton,
      scrollUp,
      scrollDown,
      panelX,
      panelY,
      panelWidth,
      panelHeight,
      colPadding,
      listColWidth,
      previewColWidth,
      previewStartX,
      previewCenterX,
      rowHeight,
      listStartY,
    };
  }

  private computeCellLayout(
    previewStartX: number,
    baseY: number,
    previewColWidth: number,
    panelHeight: number
  ): Array<{ cx: number; cy: number; cellW: number; cellH: number; bodyBottomY: number }> {
    const gridTopY = baseY + 36;
    const gridBottomY = baseY + panelHeight - 16;
    const gridHeight = gridBottomY - gridTopY;
    const cellGap = 6;
    const cellW = (previewColWidth - cellGap) / 2;
    const cellH = (gridHeight - cellGap) / 2;

    const leftColX = previewStartX + cellW / 2;
    const rightColX = leftColX + cellW + cellGap;
    const topRowY = gridTopY + cellH / 2;
    const bottomRowY = topRowY + cellH + cellGap;
    const topBodyBottomY = topRowY + cellH / 2 - 4;
    const bottomBodyBottomY = bottomRowY + cellH / 2 - 4;

    return [
      { cx: leftColX, cy: topRowY, cellW, cellH, bodyBottomY: topBodyBottomY }, // UP
      { cx: rightColX, cy: topRowY, cellW, cellH, bodyBottomY: topBodyBottomY }, // DOWN
      { cx: leftColX, cy: bottomRowY, cellW, cellH, bodyBottomY: bottomBodyBottomY }, // LEFT
      { cx: rightColX, cy: bottomRowY, cellW, cellH, bodyBottomY: bottomBodyBottomY }, // RIGHT
    ];
  }

  private createPreviewCell(
    direction: Direction,
    cx: number,
    cy: number,
    cellW: number,
    cellH: number,
    bodyBottomY: number
  ): PreviewCell {
    const headCenterY =
      bodyBottomY - this.constants.HEAD_OFFSET_FROM_BODY_BOTTOM - this.constants.HEAD_FRAME_HEIGHT / 2;
    const tileCenterX = cx;
    const tileCenterY = bodyBottomY - TILE_SIZE / 2;

    const box = this.scene.add
      .rectangle(cx, cy, cellW, cellH, 0x000000, 0.8)
      .setOrigin(0.5)
      .setStrokeStyle(1, 0xffffff)
      .setDepth(1100)
      .setScrollFactor(0)
      .setVisible(false);

    const bodyId = this.callbacks.getLocalPlayerBodyId();
    const body = this.scene.add
      .sprite(cx, bodyBottomY, getBodySpriteKey(bodyId))
      .setOrigin(0.5, 1)
      .setVisible(false)
      .setDepth(1100)
      .setScrollFactor(0);

    // Arma encima del body. Se crea con la textura del body como placeholder
    // (los sheets de la galería se cargan async); updatePreview le pone la real.
    const weapon = this.scene.add
      .sprite(cx + this.weaponOffsetX(direction), bodyBottomY + this.constants.WEAPON_OFFSET_Y, getBodySpriteKey(bodyId))
      .setOrigin(0.5, 1)
      .setVisible(false)
      .setDepth(1100.5)
      .setScrollFactor(0);

    const head = this.scene.add
      .sprite(cx, headCenterY, getHeadSpriteKey(this.callbacks.getLocalPlayerHeadId()))
      .setOrigin(0.5, 0.5)
      .setVisible(false)
      .setDepth(1101)
      .setScrollFactor(0);

    const vLine = this.scene.add
      .rectangle(tileCenterX, cy, 0.5, cellH, 0x00ff00, 0.8)
      .setOrigin(0.5, 0.5)
      .setDepth(1101)
      .setScrollFactor(0)
      .setVisible(false);

    const hLine = this.scene.add
      .rectangle(cx, tileCenterY, cellW, 0.5, 0x00ff00, 0.8)
      .setOrigin(0.5, 0.5)
      .setDepth(1101)
      .setScrollFactor(0)
      .setVisible(false);

    const label = this.scene.add
      .text(cx, cy - cellH / 2 + 8, CELL_DIR_LABEL[direction], withGameFont({
        fontSize: '10px',
        color: '#aaaaaa',
      }))
      .setOrigin(0.5, 0.5)
      .setDepth(1102)
      .setScrollFactor(0)
      .setVisible(false);

    return {
      direction,
      cx,
      cy,
      cellW,
      cellH,
      bodyBottomY,
      box,
      body,
      weapon,
      head,
      vLine,
      hLine,
      label,
    };
  }

  private handleListClick(index: number): void {
    const globalIndex = this.scrollOffset + index;
    const entry = this.weapons[globalIndex];
    if (!entry) return;
    this.selectedName = entry.name;
    this.refreshContents();
  }

  private refreshContents(): void {
    if (!this.panel) return;

    const rows = this.panel.list.length;
    const maxOffset = Math.max(0, this.weapons.length - rows);
    this.scrollOffset = Phaser.Math.Clamp(this.scrollOffset, 0, maxOffset);

    for (let i = 0; i < this.panel.list.length; i += 1) {
      const txt = this.panel.list[i];
      const entry = this.weapons[this.scrollOffset + i];
      if (entry) {
        txt.setText(entry.label);
        txt.setColor(entry.name === this.selectedName ? '#ffff00' : '#ffffff');
      } else {
        txt.setText('');
      }
    }

    if (!this.weapons.some((w) => w.name === this.selectedName)) {
      this.selectedName = this.weapons[0]?.name ?? '';
    }

    this.updatePreview(this.selectedName);
  }

  /** Aplica el arma seleccionada (por nombre de archivo) sobre el body local. */
  private updatePreview(name: string): void {
    if (!this.panel) return;
    this.selectedName = name;

    const bodyId = this.callbacks.getLocalPlayerBodyId();
    const headId = this.callbacks.getLocalPlayerHeadId();
    const bodyOffsetX = getBodyCenterOffset(bodyId);
    const bodyTextureKey = getBodySpriteKey(bodyId);
    const weaponTextureKey = this.galleryKey(name);
    const hasWeaponTex = name.length > 0 && this.scene.textures.exists(weaponTextureKey);

    for (const cell of this.panel.previewCells) {
      const bodyAnimKey = getBodyAnimKey(bodyId, cell.direction);
      const headCenterY =
        cell.bodyBottomY - this.constants.HEAD_OFFSET_FROM_BODY_BOTTOM - this.constants.HEAD_FRAME_HEIGHT / 2;

      cell.body
        .setOrigin(0.5, 1)
        .setTexture(bodyTextureKey)
        .setPosition(cell.cx + bodyOffsetX, cell.bodyBottomY)
        .setVisible(true);
      if (cell.body.anims.currentAnim?.key !== bodyAnimKey || !cell.body.anims.isPlaying) {
        cell.body.anims.play(bodyAnimKey, true);
      }

      // Arma: misma posición/offset que in-game. NO usa animación propia: se
      // FRAME-LOCKEA al cuerpo en frameLockWeapons() cada tick (igual que
      // in-game). Anims independientes arrancan en ticks distintos y se
      // desfasan; copiar el frame del body garantiza sincronía perfecta.
      if (hasWeaponTex) {
        const sheetOff = getWeaponSheetOffsetByName(name);
        cell.weapon
          .setOrigin(0.5, 1)
          .setTexture(weaponTextureKey)
          .setPosition(
            cell.cx + this.weaponOffsetX(cell.direction) + sheetOff.x,
            cell.bodyBottomY + this.constants.WEAPON_OFFSET_Y + sheetOff.y
          )
          .setVisible(true);
        cell.weapon.anims.stop();
        cell.weapon.setFrame(getWeaponIdleFrame(cell.direction));
      } else {
        cell.weapon.anims.stop();
        cell.weapon.setVisible(false);
      }

      if (headId > 0) {
        cell.head
          .setOrigin(0.5, 0.5)
          .setTexture(getHeadSpriteKey(headId))
          .setFrame(getHeadFrameForDirection(cell.direction))
          .setPosition(cell.cx, headCenterY)
          .setVisible(true);
      } else {
        cell.head.setVisible(false);
      }
    }

    // Reflejar la selección sobre el personaje real (solo cliente) mientras el
    // panel está abierto.
    if (this.isOpen) {
      this.callbacks.onSelectWeapon(name);
    }
  }

  private setVisible(visible: boolean): void {
    if (!this.panel) return;
    const panel = this.panel;
    panel.bg.setVisible(visible).setActive(visible);
    panel.listBg.setVisible(visible).setActive(visible);
    panel.title.setVisible(visible).setActive(visible);
    panel.list.forEach((txt) => txt.setVisible(visible).setActive(visible));
    for (const cell of panel.previewCells) {
      cell.box.setVisible(visible).setActive(visible);
      cell.body.setVisible(visible).setActive(visible);
      cell.weapon.setVisible(visible).setActive(visible);
      cell.head.setVisible(visible).setActive(visible);
      cell.vLine.setVisible(visible).setActive(visible);
      cell.hLine.setVisible(visible).setActive(visible);
      cell.label.setVisible(visible).setActive(visible);
    }
    panel.closeButton.setVisible(visible).setActive(visible);
    panel.scrollUp.setVisible(visible).setActive(visible);
    panel.scrollDown.setVisible(visible).setActive(visible);
    if (visible) {
      this.refreshContents();
    } else {
      // Al cerrar/ocultar, quitar el preview sobre el personaje real.
      this.callbacks.onSelectWeapon('');
    }
  }

  private move(newX: number, newY: number): void {
    if (!this.panel) return;
    const panel = this.panel;
    panel.panelX = newX;
    panel.panelY = newY;

    const { panelWidth, panelHeight, colPadding, listColWidth, previewColWidth, rowHeight } = panel;
    const baseX = newX - panelWidth / 2;
    const baseY = newY - panelHeight / 2;
    const leftStartX = baseX + colPadding;
    const previewStartX = leftStartX + listColWidth + colPadding;
    const previewCenterX = previewStartX + previewColWidth / 2;
    const listStartY = baseY + 36;

    panel.bg.setPosition(newX, newY);
    panel.listBg.setPosition(leftStartX + listColWidth / 2, newY);
    panel.title.setPosition(newX, baseY + 16);
    panel.scrollUp.setPosition(leftStartX + listColWidth - 8, baseY + 10);
    panel.scrollDown.setPosition(leftStartX + listColWidth - 8, baseY + panelHeight - 26);

    panel.list.forEach((txt, i) => {
      txt.setPosition(leftStartX + 6, listStartY + i * rowHeight);
    });

    const cellLayout = this.computeCellLayout(previewStartX, baseY, previewColWidth, panelHeight);
    panel.previewCells.forEach((cell, idx) => {
      const { cx, cy, cellW, cellH, bodyBottomY } = cellLayout[idx];
      cell.cx = cx;
      cell.cy = cy;
      cell.cellW = cellW;
      cell.cellH = cellH;
      cell.bodyBottomY = bodyBottomY;

      const tileCenterY = bodyBottomY - TILE_SIZE / 2;
      cell.box.setPosition(cx, cy).setSize(cellW, cellH);
      cell.vLine.setPosition(cx, cy).setSize(0.5, cellH);
      cell.hLine.setPosition(cx, tileCenterY).setSize(cellW, 0.5);
      cell.label.setPosition(cx, cy - cellH / 2 + 8);
    });

    panel.closeButton.setPosition(newX + panelWidth / 2 - 16, baseY + 8);

    panel.previewStartX = previewStartX;
    panel.previewCenterX = previewCenterX;

    this.updatePreview(this.selectedName);
  }
}
